import { debug } from "src/utils/logger"

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Use when you wish to check if a string can be coerced into a number */
export function isNumber(s: string | number | null | undefined) {
  // return !!s && +s === +s // eslint-disable-line no-self-compare
  return !isNil(s) && s !== "" && +s === +s
}

/** Use when you wish to check if something is a string */
export function isString(thingToCheck: unknown): thingToCheck is string {
  return typeof thingToCheck === "string" || thingToCheck instanceof String
}

/** Coerce a string into a number */
export function asNumber(s: string | null | undefined): number | undefined {
  if (!isNumber(s)) {
    return undefined
  }
  return parseFloat(String(s))
}

/** Use when you wish to check if a string can be coerced into a boolean */
export function isBoolean(s: string | boolean | null | undefined) {
  return s === "false" || s === "true" || s === true || s === false
}

/** Coerce a string into a boolean */
export function asBoolean(
  s: string | boolean | null | undefined
): boolean | undefined {
  if (!isBoolean(s)) {
    return undefined
  }
  return s === "true" || s === true
}

export function stringSplice(orig: string, index: number, str: string) {
  return orig.slice(0, index) + str + orig.slice(index)
}

/** This function does not currently handle deep equality checks */
export function isEqual(obj1: object = {}, obj2: object = {}) {
  const sortedObj1 = Object.entries(obj1).sort()
  const sortedObj2 = Object.entries(obj2).sort()
  return JSON.stringify(sortedObj1) === JSON.stringify(sortedObj2)
}

export function unique<T = string>(a: T[] | undefined) {
  return [...new Set(a)]
}

/**
 * A function that checks if the passed parameter is Nil (null or undefined) and
 * narrows it's type accordingly

 * https://github.com/remeda/remeda/blob/v0.0.32/src/guards.ts#L187
 */
export function isNil<T>(data: T): data is Extract<T, null | undefined> {
  return data == null
}

/** Returns true if value is nil or an empty object/array */
function empty(value: unknown): boolean {
  if (Array.isArray(value) && value.length === 0) {
    return true
  }
  if (isObject(value) && Object.keys(value).length === 0) {
    return true
  }
  return isNil(value)
}

/**
 * This function takes any kind of generic, arbitrarily nested object and
 * returns a copy without 'empty' fields, i.e., an object where all keys that
 * point to undefined, null or an empty object are recursively removed.
 */
export function purgeEmpty<T>(o: DefinitelyObject<T>): DefinitelyObject<T> {
  if (!isObject(o)) {
    return o
  }

  // this is an object; recurse through its fields, filtering out empty objects
  const copy = Object.entries(o).filter(([_, value]) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const subObject = purgeEmpty(value)
    return !empty(subObject)
  })
  return Object.fromEntries(copy) as DefinitelyObject<T>
}

type DefinitelyObject<T> =
  Exclude<
    Extract<T, object>,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    Array<unknown> | Function | ReadonlyArray<unknown>
  > extends never
    ? { [k: string]: unknown }
    : Exclude<
        Extract<T, object>,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        Array<unknown> | Function | ReadonlyArray<unknown>
      >
/**
 * A function that checks if the passed parameter is of type Object and narrows
 * it's type accordingly.
 *
 * Inspired by Remeda's isObject function:
 * https://github.com/remeda/remeda/blob/v0.0.32/src/guards.ts#L149
 */
export function isObject<T>(data: T | object): data is DefinitelyObject<T> {
  return !!data && !Array.isArray(data) && typeof data === "object"
}

export function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^\w ]+/g, "") // remove all non-alphanumerical characters except space
    .replace(/ +/g, "-") // replace all spaces with dashes
}

export function chunk<T>(array: T[], size?: number) {
  const chunkSize = size || 1
  return array.reduce<T[][]>((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / chunkSize)
    resultArray[chunkIndex] = [...(resultArray[chunkIndex] ?? []), item]
    return resultArray
  }, [])
}

export function arrayIncludesValueOfOtherArray<T>(
  arr: readonly T[],
  comparer: readonly T[]
) {
  return arr.some((v) => comparer.includes(v))
}

/* A function that will retry a promise until it resolves or the max number of
 * retries is reached.
 *
 * @param fn - The function to retry
 * @param retries - The number of retries to attempt
 * @param delay - The delay in milliseconds between retries
 */
export async function retry<T>(
  fn: () => Promise<T>,
  { retries = 3, delay = 500 }: { retries?: number; delay?: number } = {}
): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    if (retries <= 0) {
      throw error
    }
    debug.info("Retrying after error:", error)
    await sleep(delay)
    return retry(fn, { retries: retries - 1, delay })
  }
}

/**
 * Due to the native `Array.includes()` narrowing the types a regular search for any string does not work
 * This util makes it possible to check if an array of any type includes an element of broader type
 *
 * Credit to:
 * https://www.petermekhaeil.com/til/ts-array-includes/
 *
 * @param arr The array to search in
 * @param searchElement Element to check
 * @returns True if the arr includes searchElement, false otherwise
 */
export function arrayIncludes<T extends U, U = string>(
  arr: ReadonlyArray<T>,
  searchElement: U
): searchElement is T {
  return arr.includes(searchElement as T)
}

/**
 * Groups an array of objects based on a grouping function.
 *
 * @param arr - The array to be grouped
 * @param grouper - A function that returns a string key to group by
 * @returns An array of objects containing the group label and grouped items
 *
 * @example
 * const people = [
 *   { name: 'John', age: 30 },
 *   { name: 'Jane', age: 30 },
 *   { name: 'Bob', age: 25 }
 * ];
 *
 * const grouped = groupArrayValues({
 *   arr: people,
 *   grouper: (person) => person.age.toString()
 * });
 * // Returns:
 * // [
 * //   { label: '30', items: [{ name: 'John', age: 30 }, { name: 'Jane', age: 30 }] },
 * //   { label: '25', items: [{ name: 'Bob', age: 25 }] }
 * // ]
 */
export function groupArrayValues<T extends object>({
  arr,
  grouper,
}: {
  arr: T[]
  grouper: (item: T) => string
}) {
  const groupedMap = arr.reduce<Map<string, { label: string; items: T[] }>>(
    (map, curr) => {
      const label = grouper(curr)

      const group = map.get(label)

      if (group) {
        group.items.push(curr)
      } else {
        map.set(label, { label, items: [curr] })
      }

      return map
    },
    new Map()
  )

  return Array.from(groupedMap.values())
}

/**
 * Strongly typed wrapper around Object.fromEntries.
 *
 * Converts an array of key-value pairs into a Record<K, V>, preserving the key type K
 * rather than falling back to the default Record<string, V>.
 *
 * This ensures that the returned object retains precise key typing, useful when
 * working with known string literal unions as keys.
 *
 * @param entries - An array of readonly [key, value] pairs
 * @returns A Record<K, V> object with preserved key and value types
 */
export function typedFromEntries<K extends string, V>(
  entries: readonly (readonly [K, V])[]
): Record<K, V> {
  return Object.fromEntries(entries) as Record<K, V>
}

/**
 * Returns a rounded percentage, or 0 if denominator is 0.
 * @param numerator - The part value
 * @param denominator - The total value
 * @returns number - Rounded percentage (0-100)
 */
export function getPercentage(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return 0
  }
  return Math.round((numerator / denominator) * 100)
}

/**
 * Counts the number of words in a text string by splitting on whitespace.
 *
 * @param text - The input text string to count words in
 * @returns The number of words in the text
 */
export function textWordCount(text: string) {
  if (text === "") {
    return 0
  }

  return text.trim().split(/\s+/).length
}

/**
 * Counts the number of characters in a text string.
 *
 * @param text - The input text string to count characters in
 * @returns The number of characters in the text
 */
export function textCharacterCount(text: string) {
  return text.length
}

/**
 * Generates a UUID v4 compliant identifier.
 * This is a replacement for crypto.randomUUID() that doesn't rely on the crypto API.
 *
 * @returns A UUID v4 string in the format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Joins a 2D array of strings into a formatted string, ignoring undefined values.
 * Each inner array is joined by spaces, and resulting strings are joined by commas.
 *
 * @example
 * concatenate([
 *   ["John", "Doe"],
 *   ["Baltzarsgatan", undefined, "9", "Malmö"]
 *   ["Sweden"]
 * ]); // "John Doe, Baltzarsgatan 9 Malmö, Sweden"
 */
export function concatenate(data: (string | undefined)[][]) {
  return data
    .map((i) => i.filter((item) => item !== undefined).join(" "))
    .filter((i) => i.length > 0)
    .join(", ")
}
